import { useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as TestIcon } from '@elementary/common-ui/assets/test-icon.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FormControls,
} from '@elementary/common-ui/components';
import {
  getFirstLettersOfName,
  mapNameToColor,
} from '@elementary/common-ui/utils/common';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { DWH } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from 'shared/services/api';
import { Test } from 'shared/types';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

const Wrapper = styled(FlexColumn)`
  gap: ${space('sm')};
  padding: ${space('lg', 0)};
  overflow: auto;
`;

const Label = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const Circle = styled.div<{ color?: string; verified?: boolean }>`
  position: relative;
  min-width: 20px;
  min-height: 20px;
  border-radius: ${radius('round')};
  text-align: center;
  background: ${({ color, theme }) =>
    color || theme.palette.identity.avatarFallback};
`;

const TextInCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${({ theme }) => theme.palette.content.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  font-size: ${fontSize('xxxs')};
`;

const SummaryChip = styled(FlexCenter)`
  background: ${({ theme }) => theme.palette.surface.base};
  border: 1px solid ${({ theme }) => theme.palette.border.selected};
  border-radius: ${radius('lg')};
  padding: ${space('smMinus', 'sm')};
  height: 28px;
`;

type Props = {
  selectedItems: Test[];
};

const DeleteTestAccordion = ({ selectedItems }: Props) => {
  const [expanded, setExpanded] = useState<string>(
    selectedItems.length === 1 ? 'panel0' : '',
  );

  const selectedEnvId = useSelectedEnv();

  const { data: dwhData } = useQuery(
    [DWH, selectedEnvId],
    () => api.fetchDwh(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  const handleChange = (panel: string) => {
    setExpanded(expanded === panel ? '' : panel);
  };

  return (
    <Wrapper>
      {selectedItems.map(
        ({ name, owners, tags, schema, table, description }, index) => (
          <FormControls.TestAccordion
            key={`${name}-${index}`}
            expanded={expanded === `panel${index}` || false}
            onChange={() => handleChange(`panel${index}`)}
            summary={
              <Flex
                sx={{
                  gap: 3,
                  alignItems: 'center',
                }}
              >
                <Flex
                  sx={{
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <TestIcon />
                  <Typography sx={{ fontWeight: fontWeight('semibold') }}>
                    {name}
                  </Typography>
                </Flex>
                {table && (
                  <SummaryChip>
                    <Typography
                      sx={{
                        fontSize: fontSize('sm'),
                        color: 'content.secondary',
                      }}
                    >{`${dwhData?.database}.${schema}.${table}`}</Typography>
                  </SummaryChip>
                )}
              </Flex>
            }
          >
            <Flex sx={{ gap: 3, p: 3 }}>
              <Divider
                orientation="vertical"
                absolute={false}
                variant="fullWidth"
                flexItem
                sx={{ background: 'surface.selected', borderRightWidth: 5 }}
              />
              <FlexColumn sx={{ py: 1, gap: 3 }}>
                <FlexColumn sx={{ gap: 1 }}>
                  <Label>Owners:</Label>
                  <Flex sx={{ alignItems: 'center', gap: 2 }}>
                    {owners.map((owner, i) => (
                      <FlexCenter sx={{ gap: 1 }} key={i}>
                        <Circle color={mapNameToColor(owner)}>
                          <TextInCircle>
                            {getFirstLettersOfName(owner ?? '')}
                          </TextInCircle>
                        </Circle>
                        <Typography>{owner}</Typography>
                      </FlexCenter>
                    ))}
                  </Flex>
                </FlexColumn>

                <FlexColumn sx={{ gap: 1 }}>
                  <Label>Tags:</Label>
                  <Flex sx={{ alignItems: 'center', gap: 2 }}>
                    {tags.map((tag, i) => (
                      <FlexCenter sx={{ gap: 2 }} key={i}>
                        <Typography variant="body2">#{tag}</Typography>
                      </FlexCenter>
                    ))}
                  </Flex>
                </FlexColumn>

                <FlexColumn sx={{ gap: 1 }}>
                  <Label>Description:</Label>
                  {!!description && (
                    <Flex sx={{ alignItems: 'center', gap: 2 }}>
                      <FlexCenter sx={{ gap: 1 }}>
                        <Typography variant="body2">{description}</Typography>
                      </FlexCenter>
                    </Flex>
                  )}
                </FlexColumn>
              </FlexColumn>
            </Flex>
          </FormControls.TestAccordion>
        ),
      )}
    </Wrapper>
  );
};

export default DeleteTestAccordion;
